// ADR 5.9.26i §2 — the browser entry point. ADR 6.9.26c §1–§2 — the telemetry
// gate it reads and the two failure paths it now has.
//
// i18n is initialised *before* the first render, not inside a Suspense
// boundary: the alternative puts a frame of raw catalogue keys on screen, and
// on an RTL locale a frame of left-to-right layout that then flips.
//
// Telemetry is the opposite: the gate read is synchronous (`localStorage`), the
// SDK construction is never awaited, and the account reconcile happens *after*
// the render. Adding a cross-origin round-trip to i18n's critical path would
// trade a real flash for a real stall and make an API outage a blank page —
// which is the failure §2 exists to catch, not to cause.

import { StrictMode } from 'react'
import { createRoot, type Root } from 'react-dom/client'
import { RouterProvider } from 'react-router-dom'
import { QueryClientProvider } from '@tanstack/react-query'

import { AuthSessionProvider } from './auth/session'
import { SpeechPlayerProvider } from './features/tutor/speech/player'
import { createRouter, routes, SessionLoading } from './app/router'
import { signedOutEntry } from './app/signedOutEntry'
import { afterFirstPaint } from './app/firstPaint'
import { RootErrorBoundary, reportFatal } from './app/RootErrorBoundary'
import { StaticShellCommit } from './app/StaticShellCommit'
import { initI18n } from './i18n'
import { createAppQueryClient } from './offline/queryClient'
import { OfflineCache } from './offline/OfflineCache'
import { flushLaunchEvent, reportUndeliveredLaunch, setLaunchScheme } from './layout/analytics'
import { initTelemetry, readLocalConsent, reconcileConsent } from './telemetry'
import { flushInstallLinkOpened, flushInstallStripShown } from './telemetry/installStrip'
import { flushFieldReports } from './telemetry/field/delivery'
import { readDeviceInputs } from './telemetry/field/deviceRecord'
import { reportUnrecognisedBrowser } from './telemetry/field/installReports'
import { displayModeOf } from './pwa/displayMode'
import { latchChromeMissing } from './pwa/handoff'
import { promptAvailable, startInstallCapture } from './pwa/installPrompt'
import { registerServiceWorker } from './pwa/register'
import { initTheme } from './ui/theme'
import { initTextScale } from './display/textScale'
import { readRefreshToken } from './auth/tokenStore'
import { hasToken, preloadIfHinted, startWasm } from './wasm/preloadHint'
import { wasmUrl } from './wasm/url'
import { refuseRender, reportRecordedFailure } from './wasm/report'
import { wasmInitOutcome } from './wasm/initOutcome'
import './index.css'

// ADR 23.9.26v §3/§4 — persisted-prefix `gcTime` defaults, and the one online signal.
const queryClient = createAppQueryClient()

// Read the gate first and construct against it: ADR 30.7.26a — an SDK
// constructed and then opted out has already emitted its own automatic
// lifecycle events. Deliberately not awaited; a slow or failed SDK import must
// not be able to delay or break the render.
//
// ADR 6.9.26p §2 — and the one line that keeps the launch event from being lost
// to that same non-await. `AdaptiveShell`'s first-mount effect runs long before
// this promise settles, so `web_app_opened` is emitted into a `capture()` that
// has no PostHog yet. The latch is set on *delivery*, and this is where the
// undelivered one is retried — deterministically, once, rather than by a buffer
// inside `capture()` that would change the seam for every other event.
// `initTelemetry` never rejects, so `.then` needs no `.catch`.
//
// ADR 8.9.26b §3 — and a *second* `.then` after it, chained rather than folded
// into the first. The obvious rewrite, `.then(() => { flushLaunchEvent(); … })`,
// deletes the substring `analytics.test.tsx` pins for 6.9.26p AC11, so the
// existing expression survives verbatim and the new call lands behind it. What
// it reports is G3: a granting tab whose launch was still pending after the
// retry ran, which is the one state the delivery latch cannot recover from and
// which is indistinguishable, from outside, from a tab that denied consent.
//
// ADR 11.9.26a §1 added a third `.then` here for the wasm init's report. ADR
// 25.9.26f §4 took it out of the chain: without a static import of the facade
// the chain can settle before the init has, read an empty slot and report
// nothing, so the report now waits on both promises (`reportRecordedFailure`)
// and the chain's promise is returned for it.
//
// ADR 17.9.26k §5 / 25.9.26g §6 — and a `.then` as the chain's last arm, never
// folded into an earlier one: the install strip's `shown` is the panel's
// denominator, and
// the strip mounts with the shell — before this promise settles — so its first
// attempt is lost to the same non-await as the launch. It latches on delivery
// and parks the miss; this is the one retry. Not queued behind the wasm.
//
// A named function rather than lines inside `bootstrap`, because the
// commentary above is longer than the composition root's whole body and the
// 50-line budget counts it.
function startTelemetry(): Promise<void> {
  const consent = readLocalConsent()
  // ADR 23.9.26u §12 — a PostHog session property, so every event of an
  // installed session splits by it without touching an emitter.
  return initTelemetry(consent, { display_mode: displayModeOf(window) })
    .then(flushLaunchEvent)
    .then(() => reportUndeliveredLaunch(consent))
    .then(flushInstallLinkOpened)
    .then(flushInstallStripShown)
}

/**
 * ADR 15.10.26d §0, §3 — once `startTelemetry`'s chain has settled (so after
 * the strip's flush), the field reports' one retry, for a render failure or a
 * prompt rejection fired while Sentry was still behind `import()`; then the
 * once-per-load check for an Android browser we have not met. Hung off the
 * settled promise rather than appended to the chain, whose last arm 25.9.26g
 * AC8 pins, and returns that promise unchanged. Neither arm ever rejects.
 */
function startFieldReports(telemetry: Promise<void>): Promise<void> {
  void telemetry.then(flushFieldReports).then(() => reportUnrecognisedBrowser(readDeviceInputs(promptAvailable())))
  return telemetry
}

/**
 * ADR 1.10.26p §1 — a token-less visit to a guarded route is rewritten to
 * `/signin?next=…` before the router exists, so the router never resolves
 * Home's lazy import (and the wasm facade behind it) only to redirect. Then
 * ADR 25.9.26q §2's hint, read from the live `Location` after the rewrite.
 * The token is read once; a throwing read is "no token" for both.
 */
function enterBoot(loc: Location): boolean {
  const tokenPresent = hasToken(readRefreshToken)
  const entry = signedOutEntry(routes, { pathname: loc.pathname, search: loc.search }, tokenPresent)
  if (entry !== null) history.replaceState(null, '', entry)
  // The answer already read, replayed, so storage is read once for both decisions.
  return preloadIfHinted({ readToken: () => (tokenPresent ? 'present' : null), pathname: loc.pathname, doc: document, href: wasmUrl })
}

/** ADR 25.9.26f §4 — the facade's own chunk failed to load: as a failed entry would. */
function refuseUnloaded(root: Root, error: unknown): void {
  root.unmount()
  reportFatal(error)
}

/**
 * ADR 23.9.26u §8 — the prompt capture; ADR 6.10.26b §4 — beside it, a Chrome
 * intent's `handoff=missing` fallback, held before 1.10.26p's rewrite and
 * 6.10.26a's strip of the query can change `location.search`.
 */
function startInstallSignals(): void {
  startInstallCapture(window)
  latchChromeMissing(location.search)
}

async function bootstrap() {
  // ADR 23.9.26u §8 — first, before any `await`: `beforeinstallprompt` can fire
  // early, and a listener attached after it fired never hears it.
  startInstallSignals()
  const telemetry = startFieldReports(startTelemetry())

  // ADR 8.9.26g §2 — before the first render and before `initI18n`, for the
  // reason i18n itself is awaited: a frame drawn in the wrong scheme and then
  // repainted is the flash the whole arrangement exists to avoid. It is
  // synchronous (`localStorage` + `matchMedia`), so it delays nothing.
  //
  // ADR 9.9.26j §1 — and the resolved `Scheme` is no longer discarded. This runs
  // after `startTelemetry()` and before the first render, so it beats
  // `AdaptiveShell`'s first-mount effect on the ordinary path and the chained
  // `flushLaunchEvent` on the retry path alike; `web_app_opened` carries it.
  setLaunchScheme(initTheme())
  // ADR 30.9.26i §4 — the text size, for the same no-wrong-frame reason, and
  // before the first render so its resize listener precedes the shell's.
  initTextScale()
  // ADR 1.10.26p §1 then 25.9.26q §2 — synchronous, never throws; see `enterBoot`.
  const hinted = enterBoot(location)

  await initI18n()
  const container = document.getElementById('root')
  if (!container) throw new Error('missing #root')
  const root = renderApp(container)

  // ADR 25.9.26f §4 — the wasm after the render, never before it: the boot
  // graph does not reach the facade, so the sign-in form paints without it.
  // This warms the module for a submit or a navigation, refuses over the live
  // root on a recorded failure, and reports once telemetry is up. ADR 25.9.26q
  // §3 — at once when hinted, else after `load` + first paint (1.10.26p) or credential focus.
  const facade = startWasm(hinted, { doc: document, win: window, whenPainted: (fn) => afterFirstPaint(window, fn), load: () => import('./wasm') })
  void reportRecordedFailure(telemetry, facade)
  refuseOnFailure(facade, root)

  // ADR 23.9.26u §6 — the one registration, production builds only. Vitest's
  // mode is `test` and no test imports this file, so no suite can reach it.
  if (import.meta.env.PROD) {
    registerServiceWorker({ nav: navigator, doc: document, win: window, reload: () => location.reload(), now: Date.now, session: () => window.sessionStorage })
  }

  // After the render, never before it. Until 5.9.26p supplies a bearer this can
  // only take its failure branch, which keeps the local record — the merge rule
  // is pinned by AC4 rather than by this call site.
  void reconcileConsent()
}

/**
 * ADR 25.9.26f §4 — refuse over the live root on a recorded init failure, or
 * unmount and report a facade chunk that never loaded. ADR 16.10.26p §3 — the
 * failure is the init outcome, awaited only after the import resolved: Safari
 * may resolve it before the facade's body ran, and a rejected import never
 * settles the outcome. Declared after `bootstrap`, like `renderApp`.
 */
function refuseOnFailure(facade: Promise<unknown>, root: Root): void {
  void facade.then(() => wasmInitOutcome).then(
    (failure) => refuseRender(failure, root),
    (error: unknown) => refuseUnloaded(root, error),
  )
}

/**
 * The composition root, mounted into `#root`.
 *
 * Extracted from `bootstrap` when ADR 19.9.26c added `StaticShellCommit` and
 * its comment: the tree plus the four ADR notes explaining its nesting is most
 * of the 50-line budget on its own, and the smallest behaviour-preserving
 * split is the one along "decide, then render". `bootstrap` keeps the ordering
 * decisions — telemetry first, the theme before i18n, the wasm after the
 * render — and this holds only the shape of the tree.
 *
 * Declared **after** `bootstrap`, reached by hoisting, so the source reads in
 * the order the steps run. Returns its `Root` because ADR 25.9.26f §4's
 * refusal unmounts it before revealing the fatal template.
 */
function renderApp(container: HTMLElement): Root {
  const root = createRoot(container)
  root.render(
    <StrictMode>
      {/* ADR 19.9.26c §1 — first, and outside the boundary below: the static
          shell's removal is conditional on React committing, not on the
          committed page being the application rather than its failure UI. */}
      <StaticShellCommit />
      {/* ADR 6.9.26c §2 — inside StrictMode but outside everything else, so a
          throw from any *provider* is a fallback rather than a blank page. It
          cannot catch `bootstrap` itself; `.catch` below does that. ADR 8.9.26p
          §3 — and it does not catch a throw from a **route** either, which this
          comment claimed until that ADR: in a v6 data router the router's own
          internal boundary gets there first, so the route half is
          `router.tsx`'s `errorElement`, `app/RouteFailure.tsx`. */}
      <RootErrorBoundary>
        <QueryClientProvider client={queryClient}>
          {/* ADR 5.9.26p §5 — outside the router on purpose: the guards inside it
              read this context, so it has to be their ancestor, and the bootstrap
              refresh should start once per tab rather than once per navigation. */}
          <AuthSessionProvider>
            <SpeechPlayerProvider>
              {/* ADR 23.9.26v §3 — innermost: it needs the session's `userId`,
                  and holds every query until the restore lands. */}
              <OfflineCache>
                {/* ADR 24.9.26ag §4 — the first-load fallback while a deep
                    link's lazy route resolves. Honoured only while
                    `v7_partialHydration` is off; that flag wants a
                    `HydrateFallback` instead, and AC4 fails until it moves. */}
                <RouterProvider router={createRouter()} fallbackElement={<SessionLoading />} />
              </OfflineCache>
            </SpeechPlayerProvider>
          </AuthSessionProvider>
        </QueryClientProvider>
      </RootErrorBoundary>
    </StrictMode>,
  )
  return root
}

bootstrap().catch(reportFatal)
